import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Slider } from "./ui/slider";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import {
    ZoomIn, ZoomOut, Maximize, ChevronLeft, ChevronRight,
    Upload, Loader2, Eye, EyeOff, Move, Grip, RotateCcw, Settings2, Tag, Hand, MousePointer2, Sparkles, Eraser,
    AlignHorizontalDistributeCenter, AlignVerticalDistributeCenter,
    Bold, Italic, Underline as UnderlineIcon, Strikethrough, AlignLeft, AlignCenter, AlignRight,
    AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd, Scan,
} from "lucide-react";
import { bubbleBox, BUBBLE_LH } from "../lib/bubbleText";
import { bubbleBase, effBubbleStyle, bubblePaint, BUBBLE_FONTS, bubbleFont } from "../lib/bubbleStyle";
import { AiReviewOverlay } from "./AiReviewOverlay";
import { rgba, colorMode } from "../lib/posColors";
import { BubbleColorControls, BubbleScope } from "./BubbleColorControls";
import { ColorModeControls } from "./ColorModeControls";
import { computeUnits, layoutUnits, trayPanelSize, colsFor } from "../lib/bubbleLayout";
import { useI18n } from "../context/LanguageContext";
import { CommentLayer } from "./comments/CommentLayer";
import { CommentToolbar } from "./comments/CommentToolbar";
import { defaultStyle } from "../lib/annotGeom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const TRAY_BUBBLE_PX = 30;

const AI_FIELD_COLORS = { name: "#eab308", width: "#3b82f6", height: "#22c55e", qty: "#ef4444" };

function PageInput({ page, numPages, onGo }) {
    const [v, setV] = useState(String(page));
    useEffect(() => { setV(String(page)); }, [page]);
    const go = () => {
        const n = parseInt(v, 10);
        if (!n || !numPages) { setV(String(page)); return; }
        const p = Math.min(Math.max(1, n), numPages);
        setV(String(p));
        if (p !== page) onGo(p);
    };
    return (
        <span data-testid="pdf-page-indicator" className="flex items-center gap-1 text-xs font-mono text-slate-300 whitespace-nowrap">
            <input data-testid="pdf-page-input" value={v} inputMode="numeric"
                onChange={(e) => setV(e.target.value.replace(/\D/g, ""))}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => { if (e.key === "Enter") { go(); e.target.blur(); } else if (e.key === "Escape") { setV(String(page)); e.target.blur(); } }}
                onBlur={() => setV(String(page))}
                style={{ width: `${Math.max(2, String(numPages || 1).length) + 1.5}ch` }}
                className="h-7 text-center rounded border border-[#1e293b] bg-[#0b0f17] text-slate-100 focus:outline-none focus:border-sky-500" />
            <span data-testid="pdf-page-total">/ {numPages || 1}</span>
        </span>
    );
}

export default function PdfViewer({
    pdfData, hasPdf, uploading, onUpload,
    rows, rowColors, tray, setTray, unitOffsets, setUnitOffsets, visibleRowIds,
    bubbleStyles, setBubbleStyles,
    activeRowId, setActiveRowId, jump, pageNames, setPageNames,
    onInteractionStart, onInteractionEnd, onAiAnalyze, onOpenAiHistory, onExportPdf, exporting, onClearAi, hasAi,
    comments, cm, selectedCommentId, setSelectedCommentId, onReplyComment, readOnly, dateFmt,
    aiReview, aiMarking, setAiMarking, projectId, locate, flashIds, onLocateMiss,
}) {
    const { t } = useI18n();
    const [cTool, setCTool] = useState(null);
    const [cStyles, setCStyles] = useState({});
    const [showComments, setShowComments] = useState(true);
    const [cMulti, setCMulti] = useState([]);
    const { user: authUser } = useAuth();
    const styleFor = (tp) => ({ ...defaultStyle(tp), fontSize: authUser?.comment_font_size || 12, ...(cStyles[tp] || {}) });
    const setCStyle = (tp, patch) => setCStyles((s) => ({ ...s, [tp]: { ...defaultStyle(tp), ...(s[tp] || {}), ...patch } }));
    const canvasRef = useRef(null);
    const wrapperRef = useRef(null);
    const scrollRef = useRef(null);
    const renderTaskRef = useRef(null);
    const dragRef = useRef(null);
    const trayElRef = useRef(null);
    const suppressCenterRef = useRef(false);
    const sizeRef = useRef({ W: 0, H: 0, trayX: 0, trayY: 0 });
    const spaceStateRef = useRef({});
    const toolRef = useRef("pointer");
    const hKeyRef = useRef(false);
    const pendingZoomRef = useRef(null);
    const rootRef = useRef(null);
    const locateRef = useRef(null);
    const flashSet = new Set(flashIds || []);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [page, setPage] = useState(tray?.page || 1);
    const [textItems, setTextItems] = useState([]);
    const reviewOn = !!aiReview;
    // PDF text items of the current page (normalized boxes) so the user can click any text and assign it while reviewing AI.
    useEffect(() => {
        if (!reviewOn || !pdfDoc || page < 1 || page > pdfDoc.numPages) { setTextItems([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const pg = await pdfDoc.getPage(page);
                const vp = pg.getViewport({ scale: 1 });
                const tc = await pg.getTextContent();
                if (cancelled) return;
                const out = [];
                for (const it of tc.items) {
                    const s = (it.str || "").trim();
                    if (!s || !it.transform) continue;
                    const [a, b, , , e, f] = it.transform;
                    const len = Math.hypot(a, b) || 1, ux = a / len, uy = b / len;
                    const h = it.height || len, w = it.width || s.length * h * 0.5;
                    const pts = [[e, f - h * 0.15], [e + ux * w, f + uy * w - h * 0.15], [e - uy * h, f + ux * h], [e + ux * w - uy * h, f + uy * w + ux * h]]
                        .map(([x, y]) => vp.convertToViewportPoint(x, y));
                    const xs = pts.map((p) => p[0] / vp.width), ys = pts.map((p) => p[1] / vp.height);
                    out.push({ s, box: [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)] });
                    if (out.length >= 6000) break;
                }
                setTextItems(out);
            } catch { setTextItems([]); }
        })();
        return () => { cancelled = true; };
    }, [reviewOn, pdfDoc, page]);
    const [scale, setScale] = useState(1.1);
    const [loading, setLoading] = useState(false);
    const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
    const [selectedIds, setSelectedIds] = useState([]);
    const [bubbleScope, setBubbleScope] = useState("selected");
    const [marqueeRect, setMarqueeRect] = useState(null);
    const [vp, setVp] = useState({ w: 0, h: 0 });
    const [tool, setTool] = useState("pointer");
    const [hForceTray, setHForceTray] = useState(false);
    const prevSelRef = useRef([]);
    const [traySc, setTraySc] = useState(null);
    const [trayScroll, setTrayScroll] = useState({ l: 0, t: 0 });
    const trayScrollRaf = useRef(null);
    useEffect(() => {
        if (!traySc) return;
        const onWheel = (e) => {
            e.preventDefault(); e.stopPropagation();
            const d = e.deltaY || e.deltaX;
            if (e.shiftKey) traySc.scrollLeft += d; else { traySc.scrollTop += e.deltaY; traySc.scrollLeft += e.deltaX; }
        };
        traySc.addEventListener("wheel", onWheel, { passive: false });
        return () => traySc.removeEventListener("wheel", onWheel);
    }, [traySc]);
    const onTrayScroll = (e) => {
        const el = e.currentTarget;
        if (trayScrollRaf.current) return;
        trayScrollRaf.current = requestAnimationFrame(() => { trayScrollRaf.current = null; setTrayScroll({ l: el.scrollLeft, t: el.scrollTop }); });
    };
    const [aiBusy, setAiBusy] = useState(false);
    const [changeConfirm, setChangeConfirm] = useState(false);
    const changeInputRef = useRef(null);
    const bResizeRef = useRef(null);
    const applyStyleRef = useRef(null);
    useEffect(() => {
        const move = (e) => {
            const r = bResizeRef.current;
            if (!r) return;
            const bw = Math.max(0.15, (r.w0 + 2 * (e.clientX - r.sx)) / r.px);
            const bh = Math.max(0.15, (r.h0 + (e.clientY - r.sy)) / r.px);
            applyStyleRef.current("bw", Math.round(bw * 100) / 100, [r.id]);
            applyStyleRef.current("bh", Math.round(bh * 100) / 100, [r.id]);
        };
        const up = () => {
            if (!bResizeRef.current) return;
            bResizeRef.current = null;
            if (onInteractionEnd) onInteractionEnd();
        };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    }, [onInteractionEnd]);

    const allUnits = computeUnits(rows, rowColors);
    const visSet = visibleRowIds ? new Set(visibleRowIds) : null;
    const units = visSet ? allUnits.filter((u) => visSet.has(u.row_id)) : allUnits;
    const unplacedCount = units.filter((u) => !unitOffsets[u.unitId]).length;
    const COLS = colsFor(unplacedCount);

    sizeRef.current = { W: canvasSize.w, H: canvasSize.h, page, vw: vp.w, vh: vp.h };
    spaceStateRef.current = { units, unitOffsets, page };
    toolRef.current = tool;

    // Load document
    useEffect(() => {
        if (!pdfData) { setPdfDoc(null); setNumPages(0); return; }
        let cancelled = false;
        setLoading(true);
        const task = pdfjsLib.getDocument({ data: pdfData.slice(0) });
        task.promise.then((doc) => {
            if (cancelled) return;
            setPdfDoc(doc);
            setNumPages(doc.numPages);
        }).catch(() => { }).finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [pdfData]);

    // Render page: draw into an offscreen canvas and swap when ready (no white flash); rendered pages are cached.
    const pageCacheRef = useRef(new Map());
    const edgeRef = useRef(null);
    const navRef = useRef({ page: 1, numPages: 0 });
    navRef.current = { page, numPages };
    const lastShownRef = useRef(0);
    const putCache = (key, c) => {
        const cache = pageCacheRef.current;
        cache.set(key, c);
        let px = 0;
        cache.forEach((v) => { px += v.width * v.height; });
        while (cache.size > 3 && px > 40e6) {
            const k = cache.keys().next().value, v = cache.get(k);
            px -= v.width * v.height; cache.delete(k);
        }
    };
    useEffect(() => { pageCacheRef.current = new Map(); }, [pdfDoc]);
    useEffect(() => {
        if (!pdfDoc) return;
        if (page < 1 || page > pdfDoc.numPages) { setPage(Math.min(Math.max(1, page), pdfDoc.numPages)); return; }
        let cancelled = false;
        (async () => {
            const pg = await pdfDoc.getPage(page);
            const viewport = pg.getViewport({ scale });
            const canvas = canvasRef.current;
            if (!canvas || cancelled) return;
            const w = Math.floor(viewport.width), h = Math.floor(viewport.height);
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            setCanvasSize({ w: viewport.width, h: viewport.height });
            const sc = scrollRef.current;
            if (edgeRef.current && sc) { sc.scrollTop = edgeRef.current === "top" ? 0 : sc.scrollHeight; edgeRef.current = null; }
            const blit = (src) => {
                if (canvas.width !== src.width || canvas.height !== src.height) { canvas.width = src.width; canvas.height = src.height; }
                canvas.getContext("2d").drawImage(src, 0, 0);
                if (lastShownRef.current && lastShownRef.current !== page) {
                    const d = page > lastShownRef.current ? 24 : -24;
                    canvas.animate?.([{ opacity: 0.35, transform: `translateY(${d}px)` }, { opacity: 1, transform: "none" }], { duration: 180, easing: "ease-out" });
                }
                lastShownRef.current = page;
            };
            const key = `${page}@${scale}`;
            const cache = pageCacheRef.current;
            if (cache.has(key)) { const c = cache.get(key); cache.delete(key); cache.set(key, c); blit(c); return; }
            if (renderTaskRef.current) { try { renderTaskRef.current.cancel(); } catch { /**/ } }
            const off = document.createElement("canvas");
            off.width = w; off.height = h;
            const rt = pg.render({ canvasContext: off.getContext("2d"), viewport });
            renderTaskRef.current = rt;
            try { await rt.promise; } catch { return; }
            if (cancelled) return;
            blit(off);
            putCache(key, off);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfDoc, page, scale]);

    // Preload neighbouring pages in the background so flipping is instant.
    useEffect(() => {
        if (!pdfDoc) return;
        let cancelled = false;
        const tasks = [];
        const id = setTimeout(async () => {
            for (const p of [page + 1, page - 1, page + 2]) {
                if (cancelled) return;
                const key = `${p}@${scale}`;
                if (p < 1 || p > pdfDoc.numPages || pageCacheRef.current.has(key)) continue;
                const pg = await pdfDoc.getPage(p);
                if (cancelled) return;
                const vp = pg.getViewport({ scale });
                const off = document.createElement("canvas");
                off.width = Math.floor(vp.width); off.height = Math.floor(vp.height);
                const rt = pg.render({ canvasContext: off.getContext("2d"), viewport: vp });
                tasks.push(rt);
                try { await rt.promise; } catch { return; }
                if (!cancelled) putCache(key, off);
            }
        }, 200);
        return () => { cancelled = true; clearTimeout(id); tasks.forEach((rt) => { try { rt.cancel(); } catch { /**/ } }); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfDoc, page, scale]);

    // Page Up / Page Down flip pages (ignored while typing).
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== "PageDown" && e.key !== "PageUp") return;
            const tag = (e.target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
            const { page: p, numPages: n } = navRef.current;
            const np = e.key === "PageDown" ? Math.min(n, p + 1) : Math.max(1, p - 1);
            e.preventDefault();
            if (np !== p) { edgeRef.current = "top"; setPage(np); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Jump to a specific page (triggered from the table's position-pages menu)
    useEffect(() => {
        if (jump && jump.page) setPage(jump.page);
    }, [jump]);

    // Tray follows the currently viewed page
    useEffect(() => {
        setTray((t) => (t.page === page ? t : { ...t, page }));
    }, [page, setTray]);

    // Table -> PDF navigation happens only on an explicit locate request (click on the row's colour bubble).
    useEffect(() => {
        if (!locate) return;
        const offs = unitOffsets || {};
        const placed = units.filter((u) => u.row_id === locate.rowId && offs[u.unitId]);
        const pref = locate.page || page;
        let target = null;
        if (placed.length) {
            const o = offs[(placed.find((u) => (offs[u.unitId].page || 1) === pref) || placed[0]).unitId];
            target = { pg: o.page || 1, fx: o.fx, fy: o.fy };
        } else {
            const r = (rows || []).find((x) => x.id === locate.rowId);
            const bx = r?.ai?.boxes;
            const b = bx ? (bx.name || bx.width || bx.height || bx.qty) : null;
            if (b && r.ai.page) target = { pg: r.ai.page, fx: (b[0] + b[2]) / 2, fy: (b[1] + b[3]) / 2 };
        }
        if (target && numPages && (target.pg < 1 || target.pg > numPages)) target = null;
        if (!target) { onLocateMiss?.(locate.rowId); toast.error(t("pv_pos_not_found")); return; }
        if (target.pg !== page) { locateRef.current = target; setPage(target.pg); }
        if (locate.full && rootRef.current && !document.fullscreenElement) rootRef.current.requestFullscreen?.().catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locate]);

    // After switching page: keep zoom & scroll; only zoom out when the bubble is outside the visible area.
    useEffect(() => {
        const L = locateRef.current, el = scrollRef.current, wrap = wrapperRef.current;
        if (!L || L.pg !== page || !el || !wrap || !canvasSize.w) return;
        locateRef.current = null;
        const x = wrap.offsetLeft + L.fx * wrap.offsetWidth - el.scrollLeft;
        const y = wrap.offsetTop + L.fy * wrap.offsetHeight - el.scrollTop;
        if (x >= 0 && x <= el.clientWidth && y >= 0 && y <= el.clientHeight) return;
        const s = scaleRef.current;
        const fit = Math.min(el.clientWidth / (wrap.offsetWidth / s), el.clientHeight / (wrap.offsetHeight / s)) * 0.95;
        if (fit >= s) return;
        pendingZoomRef.current = { fx: L.fx, fy: L.fy, px: Math.min(Math.max(x, 0), el.clientWidth), py: Math.min(Math.max(y, 0), el.clientHeight), reset: true };
        setScale(Math.max(0.25, +fit.toFixed(2)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, canvasSize.w, canvasSize.h]);

    // Global pointer handlers for dragging the tray or an individual bubble
    const onMouseMove = useCallback((e) => {
        const d = dragRef.current;
        if (!d) return;
        if (d.kind === "pan") {
            d.moved = true;
            const sc = scrollRef.current;
            if (sc) { sc.scrollLeft = d.sl - (e.clientX - d.sx); sc.scrollTop = d.st - (e.clientY - d.sy); }
            return;
        }
        if (d.kind === "marquee") {
            const rect = wrapperRef.current?.getBoundingClientRect();
            if (!rect) return;
            d.cx = e.clientX - rect.left; d.cy = e.clientY - rect.top; d.moved = true;
            setMarqueeRect({ x: Math.min(d.sx, d.cx), y: Math.min(d.sy, d.cy), w: Math.abs(d.cx - d.sx), h: Math.abs(d.cy - d.sy) });
            return;
        }
        const { W, H } = sizeRef.current;
        if (!W) return;
        d.moved = true;
        if (d.kind === "trayResize") {
            const { vw, vh } = sizeRef.current;
            const w = clamp(d.w0 + (e.clientX - d.sx), d.minW, vw), h = clamp(d.h0 + (e.clientY - d.sy), d.minH, vh);
            setTray((t) => ({ ...t, tw: w / vw, th: h / vh }));
            return;
        }
        if (d.kind === "tray") {
            const { vw, vh } = sizeRef.current;
            const nx = clamp(d.tx + (e.clientX - d.sx) / (vw || W), 0, 0.97);
            const ny = clamp(d.ty + (e.clientY - d.sy) / (vh || H), 0, 0.97);
            setTray((t) => ({ ...t, x: nx, y: ny }));
        } else {
            const rect = wrapperRef.current.getBoundingClientRect();
            // Store an absolute page-fraction position so the bubble detaches
            // from the tray and stays put when the tray is moved.
            const fx = (e.clientX - rect.left) / W;
            const fy = (e.clientY - rect.top) / H;
            if (trayElRef.current) {
                const r = trayElRef.current.getBoundingClientRect();
                d.overTray = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
            }
            if (d.group) {
                const dfx = (e.clientX - d.sx) / W, dfy = (e.clientY - d.sy) / H;
                setUnitOffsets((o) => {
                    const n = { ...o };
                    d.ids.forEach((id) => { const s = d.starts[id]; n[id] = { ...o[id], fx: clamp(s.fx + dfx, 0, 1), fy: clamp(s.fy + dfy, 0, 1), page: s.page }; });
                    return n;
                });
            } else {
                setUnitOffsets((o) => ({ ...o, [d.unitId]: { fx, fy, page: sizeRef.current.page } }));
            }
        }
    }, [setTray, setUnitOffsets]);

    const endDrag = useCallback((e) => {
        const d = dragRef.current;
        if (d) {
            if (d.kind === "marquee") {
                const { W, H } = sizeRef.current;
                const { units: U, unitOffsets: UO, page: P } = spaceStateRef.current;
                if (d.moved && W) {
                    const x1 = Math.min(d.sx, d.cx), y1 = Math.min(d.sy, d.cy);
                    const x2 = Math.max(d.sx, d.cx), y2 = Math.max(d.sy, d.cy);
                    const ids = (U || []).filter((u) => UO[u.unitId] && (UO[u.unitId].page || 1) === P)
                        .filter((u) => { const cx = UO[u.unitId].fx * W, cy = UO[u.unitId].fy * H; return cx >= x1 && cx <= x2 && cy >= y1 && cy <= y2; })
                        .map((u) => u.unitId);
                    setSelectedIds((prev) => d.additive ? Array.from(new Set([...prev, ...ids])) : ids);
                }
                setMarqueeRect(null);
                setTimeout(() => { dragRef.current = null; onInteractionEnd && onInteractionEnd(); }, 0);
                return;
            }
            let overTray = d.overTray;
            if (d.kind === "unit" && e && trayElRef.current) {
                const r = trayElRef.current.getBoundingClientRect();
                overTray = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
            }
            if (d.kind === "unit" && d.moved && overTray) {
                if (d.group) {
                    // Returning a multi-selection to the tray requires holding H.
                    if (hKeyRef.current) {
                        setUnitOffsets((o) => { const n = { ...o }; d.ids.forEach((id) => delete n[id]); return n; });
                        setSelectedIds((prev) => prev.filter((id) => !d.ids.includes(id)));
                    }
                } else {
                    setUnitOffsets((o) => { const n = { ...o }; delete n[d.unitId]; return n; });
                    setSelectedIds((prev) => prev.filter((id) => id !== d.unitId));
                }
            }
            setTimeout(() => { dragRef.current = null; onInteractionEnd && onInteractionEnd(); }, 0);
        }
    }, [onInteractionEnd, setUnitOffsets]);

    useEffect(() => {
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", endDrag);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", endDrag);
        };
    }, [onMouseMove, endDrag]);

    // Space / Shift+Space: spread or contract the bubbles already placed on the current page.
    useEffect(() => {
        const onKey = (e) => {
            if (e.code !== "Space") return;
            const el = e.target;
            const tag = (el.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || tag === "button" || el.isContentEditable) return;
            const { units: U, unitOffsets: UO, page: P } = spaceStateRef.current;
            const placed = (U || []).filter((u) => UO[u.unitId] && (UO[u.unitId].page || 1) === P);
            if (placed.length < 2) return;
            e.preventDefault();
            const factor = e.shiftKey ? 0.88 : 1.12;
            const mx = placed.reduce((a, u) => a + UO[u.unitId].fx, 0) / placed.length;
            const my = placed.reduce((a, u) => a + UO[u.unitId].fy, 0) / placed.length;
            setUnitOffsets((o) => {
                const n = { ...o };
                placed.forEach((u) => {
                    const off = n[u.unitId];
                    n[u.unitId] = { ...off, fx: clamp(mx + (off.fx - mx) * factor, 0, 1), fy: clamp(my + (off.fy - my) * factor, 0, 1) };
                });
                return n;
            });
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [setUnitOffsets]);

    // Hold "H" to return selected bubbles to the tray by dragging them onto it.
    // Pressing H also summons the tray (even when empty) so there is a drop target.
    useEffect(() => {
        const isTyping = (el) => {
            const tag = (el?.tagName || "").toLowerCase();
            return tag === "input" || tag === "textarea" || el?.isContentEditable;
        };
        const down = (e) => {
            if (e.key !== "h" && e.key !== "H") return;
            if (isTyping(e.target)) return;
            if (e.repeat) { hKeyRef.current = true; return; }
            hKeyRef.current = true;
            setHForceTray(true);
            setTray((t) => (t.visible ? t : { ...t, visible: true }));
        };
        const up = (e) => {
            if (e.key !== "h" && e.key !== "H") return;
            hKeyRef.current = false;
            setHForceTray(false);
        };
        window.addEventListener("keydown", down);
        window.addEventListener("keyup", up);
        return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
    }, [setTray]);

    // Track the scroll viewport size so the tray can stay fixed on screen.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const update = () => setVp({ w: el.clientWidth, h: el.clientHeight });
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [hasPdf]);

    // Smooth zoom (Acrobat/Maps-like): the wheel animates a GPU CSS transform around the cursor; the page is
    // re-rendered once when the gesture settles, keeping the point under the cursor fixed. Middle button = inertial pan.
    const zoomRef = useRef({ k: 1, target: 1, raf: null, idle: null, committing: false });
    const scaleRef = useRef(scale);
    scaleRef.current = scale;
    const zoomAt = useCallback((factor, clientX, clientY) => {
        const el = scrollRef.current, wrap = wrapperRef.current, z = zoomRef.current;
        if (!el || !wrap) return;
        if (z.committing) { z.queued = (z.queued || 1) * factor; z.qx = clientX; z.qy = clientY; return; }
        if (z.k === 1 && z.target === 1) {
            const wr = wrap.getBoundingClientRect(), sr = el.getBoundingClientRect();
            const cx = clientX ?? sr.left + sr.width / 2, cy = clientY ?? sr.top + sr.height / 2;
            z.ox = clamp(cx - wr.left, 0, wrap.offsetWidth); z.oy = clamp(cy - wr.top, 0, wrap.offsetHeight);
            z.px = wr.left + z.ox - sr.left; z.py = wr.top + z.oy - sr.top;
            z.w0 = wrap.offsetWidth || 1; z.h0 = wrap.offsetHeight || 1;
            wrap.style.transformOrigin = `${z.ox}px ${z.oy}px`;
            wrap.style.willChange = "transform";
        }
        const s = scaleRef.current;
        z.target = clamp(s * z.target * factor, 0.3, 4) / s;
        const commit = () => {
            if (Math.abs(z.k - 1) < 0.002) { z.k = z.target = 1; wrap.style.transform = ""; wrap.style.willChange = ""; return; }
            const ns = +(s * z.k).toFixed(3);
            if (ns === scaleRef.current) { z.k = z.target = 1; wrap.style.transform = ""; wrap.style.willChange = ""; return; }
            z.committing = true;
            pendingZoomRef.current = { fx: z.ox / z.w0, fy: z.oy / z.h0, px: z.px, py: z.py, reset: true };
            setScale(ns);
        };
        const step = () => {
            z.k += (z.target - z.k) * 0.35;
            if (Math.abs(z.target - z.k) < 0.001) z.k = z.target;
            wrap.style.transform = `scale(${z.k})`;
            if (z.k !== z.target) { z.raf = requestAnimationFrame(step); return; }
            z.raf = null;
            clearTimeout(z.idle);
            z.idle = setTimeout(commit, 140);
        };
        clearTimeout(z.idle);
        if (!z.raf) z.raf = requestAnimationFrame(step);
    }, []);
    useEffect(() => {
        const el = scrollRef.current;
        const zr = zoomRef.current;
        if (!el) return;
        const sm = { target: null, raf: null, over: 0, lock: 0 };
        const stopSmooth = () => { if (sm.raf) cancelAnimationFrame(sm.raf); sm.raf = null; sm.target = null; };
        const smooth = () => {
            const d = sm.target - el.scrollTop, before = el.scrollTop;
            if (Math.abs(d) < 0.5) { el.scrollTop = sm.target; sm.raf = null; sm.target = null; return; }
            el.scrollTop += d * 0.3;
            if (el.scrollTop === before) { sm.raf = null; sm.target = null; return; }
            sm.raf = requestAnimationFrame(smooth);
        };
        const flip = (dir) => {
            const { page: p, numPages: n } = navRef.current, np = p + dir;
            if (np < 1 || np > n) return false;
            stopSmooth();
            edgeRef.current = dir > 0 ? "top" : "bottom";
            setPage(np);
            return true;
        };
        const onWheel = (e) => {
            if (e.target.closest?.("[data-testid=bubble-tray]")) return;
            e.preventDefault();
            const k = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1;
            let dy = e.deltaY * k, dx = e.deltaX * k;
            if (e.ctrlKey) { zoomAt(Math.exp(-clamp(dy, -240, 240) * 0.0022), e.clientX, e.clientY); return; }
            if (e.shiftKey && !dx) { dx = dy; dy = 0; }
            if (dx) el.scrollLeft += dx;
            if (!dy) return;
            const max = el.scrollHeight - el.clientHeight, cur = sm.target ?? el.scrollTop, now = performance.now();
            if ((dy > 0 && cur >= max - 1) || (dy < 0 && cur <= 1)) {
                if (now < sm.lock) return;
                sm.over = Math.sign(sm.over) === Math.sign(dy) ? sm.over + dy : dy;
                if (Math.abs(sm.over) >= 100) { sm.over = 0; if (flip(dy > 0 ? 1 : -1)) sm.lock = now + 350; }
                return;
            }
            sm.over = 0;
            sm.target = clamp(cur + dy, 0, max);
            if (!sm.raf) sm.raf = requestAnimationFrame(smooth);
        };
        let pan = null, inertia = null;
        const onDown = (e) => {
            if (e.button !== 1) return;
            e.preventDefault();
            if (inertia) { cancelAnimationFrame(inertia); inertia = null; }
            pan = { x: e.clientX, y: e.clientY, t: performance.now(), vx: 0, vy: 0 };
            el.style.cursor = "grabbing";
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
        };
        const onMove = (e) => {
            if (!pan) return;
            const now = performance.now(), dt = Math.max(1, now - pan.t);
            const dx = e.clientX - pan.x, dy = e.clientY - pan.y;
            el.scrollLeft -= dx; el.scrollTop -= dy;
            pan.vx = 0.8 * (dx / dt) + 0.2 * pan.vx; pan.vy = 0.8 * (dy / dt) + 0.2 * pan.vy;
            pan.x = e.clientX; pan.y = e.clientY; pan.t = now;
        };
        const onUp = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            el.style.cursor = "";
            if (!pan) return;
            let { vx, vy } = pan, last = performance.now();
            const fresh = last - pan.t < 80;
            pan = null;
            if (!fresh) return;
            const glide = (now) => {
                const dt = now - last; last = now;
                el.scrollLeft -= vx * dt; el.scrollTop -= vy * dt;
                vx *= Math.pow(0.94, dt / 16); vy *= Math.pow(0.94, dt / 16);
                inertia = Math.hypot(vx, vy) > 0.02 ? requestAnimationFrame(glide) : null;
            };
            inertia = requestAnimationFrame(glide);
        };
        const noAuto = (e) => { if (e.button === 1) e.preventDefault(); };
        el.addEventListener("wheel", onWheel, { passive: false });
        el.addEventListener("mousedown", onDown);
        el.addEventListener("auxclick", noAuto);
        return () => {
            stopSmooth();
            el.removeEventListener("wheel", onWheel);
            el.removeEventListener("mousedown", onDown);
            el.removeEventListener("auxclick", noAuto);
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            if (inertia) cancelAnimationFrame(inertia);
            if (zr.raf) cancelAnimationFrame(zr.raf);
            clearTimeout(zr.idle);
        };
    }, [hasPdf, zoomAt]);

    // After a zoom commit re-sizes the page, keep the cursor point stationary and drop the temporary transform.
    useLayoutEffect(() => {
        const p = pendingZoomRef.current;
        const el = scrollRef.current, wrap = wrapperRef.current;
        if (!p || !el || !wrap) return;
        if (p.reset) {
            const z = zoomRef.current; wrap.style.transform = ""; wrap.style.willChange = ""; z.k = z.target = 1; z.committing = false;
            if (z.queued) { const f = z.queued, qx = z.qx, qy = z.qy; z.queued = null; requestAnimationFrame(() => zoomAt(f, qx, qy)); }
        }
        el.scrollLeft = wrap.offsetLeft + p.fx * wrap.offsetWidth - p.px;
        el.scrollTop = wrap.offsetTop + p.fy * wrap.offsetHeight - p.py;
        pendingZoomRef.current = null;
    }, [canvasSize.w, canvasSize.h, zoomAt]);

    const startTrayDrag = (e) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        if (onInteractionStart) onInteractionStart();
        dragRef.current = { kind: "tray", sx: e.clientX, sy: e.clientY, tx: tray.x, ty: tray.y, moved: false };
    };
    const startTrayResize = (e, box, minW, minH) => {
        if (e.button !== 0) return;
        e.stopPropagation(); e.preventDefault();
        if (onInteractionStart) onInteractionStart();
        dragRef.current = { kind: "trayResize", sx: e.clientX, sy: e.clientY, w0: box.w, h0: box.h, minW, minH, moved: false };
    };
    const startUnitDrag = (e, unitId) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        e.preventDefault();
        if (e.detail <= 1) prevSelRef.current = selectedIds;
        if (onInteractionStart) onInteractionStart();
        const group = selectedIds.includes(unitId) && selectedIds.length > 1;
        if (group) {
            const starts = {};
            selectedIds.forEach((id) => { if (unitOffsets[id]) starts[id] = { fx: unitOffsets[id].fx, fy: unitOffsets[id].fy, page: unitOffsets[id].page }; });
            dragRef.current = { kind: "unit", unitId, group: true, ids: Object.keys(starts), starts, sx: e.clientX, sy: e.clientY, moved: false };
        } else {
            dragRef.current = { kind: "unit", unitId, moved: false };
        }
    };
    const startPan = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        if (tool === "hand") {
            const sc = scrollRef.current;
            if (!sc) return;
            dragRef.current = { kind: "pan", sx: e.clientX, sy: e.clientY, sl: sc.scrollLeft, st: sc.scrollTop, moved: false };
        } else {
            const rect = wrapperRef.current?.getBoundingClientRect();
            if (!rect) return;
            const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
            const additive = e.ctrlKey || e.metaKey || e.shiftKey;
            dragRef.current = { kind: "marquee", sx, sy, cx: sx, cy: sy, additive, moved: false };
            if (!additive) setSelectedIds([]);
            setMarqueeRect({ x: sx, y: sy, w: 0, h: 0 });
        }
    };
    const returnToTray = (unitId) => {
        if (!unitOffsets[unitId]) return;
        const prev = prevSelRef.current || [];
        const ids = (prev.includes(unitId) ? prev : [unitId]).filter((id) => unitOffsets[id]);
        setUnitOffsets((o) => { const n = { ...o }; ids.forEach((id) => delete n[id]); return n; });
        setSelectedIds([]);
        prevSelRef.current = [];
    };
    const resetUnit = (unitId) => setUnitOffsets((o) => { const n = { ...o }; delete n[unitId]; return n; });

    const runAi = async () => {
        if (aiBusy || !onAiAnalyze) return;
        setAiBusy(true);
        try { await onAiAnalyze(); }
        finally { setAiBusy(false); }
    };

    if (!hasPdf) {
        return (
            <div className="h-full flex flex-col items-center justify-center p-8 cad-grid">
                <div className="w-20 h-20 rounded-2xl bg-[#182232] border border-[#1e293b] flex items-center justify-center mb-5">
                    <Upload className="w-9 h-9 text-slate-500" />
                </div>
                <h3 className="font-mono font-semibold text-lg text-slate-200">{t("pv_upload_title")}</h3>
                <p className="text-slate-500 text-sm mt-1 text-center max-w-xs">{t("pv_upload_sub")}</p>
                <label className="mt-6">
                    <input data-testid="pdf-upload-input" type="file" accept="application/pdf" className="hidden"
                        onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} />
                    <span className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md cursor-pointer text-sm font-medium transition-colors">
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {t("pv_choose_pdf")}
                    </span>
                </label>
            </div>
        );
    }

    const W = canvasSize.w;
    const H = canvasSize.h;
    const shape = tray.bubble_shape || "circle";
    const trayColor = tray.color || "#2563eb";
    const laid = W ? layoutUnits(units, tray, unitOffsets, W, H, COLS, page) : [];
    const laidPlaced = W ? laid.filter((u) => unitOffsets[u.unitId]) : [];
    const trayUnits = units.filter((u) => !unitOffsets[u.unitId]);
    const VW = vp.w, VH = vp.h;
    const trayScale = tray.tray_bubble_scale ?? 1;
    const vD = TRAY_BUBBLE_PX * trayScale;
    const vSTEP = vD + 12;
    const vPAD = 12;
    const vHEADER = 28;
    const vCOLS = Math.max(1, colsFor(trayUnits.length));
    const vRows = Math.max(1, Math.ceil(trayUnits.length / vCOLS));
    const vPanel = { width: vPAD * 2 + vCOLS * vSTEP, height: vHEADER + vPAD * 2 + vRows * vSTEP, header: vHEADER };
    const trayMinW = Math.max(90, vSTEP + vPAD * 2), trayMinH = vHEADER + vSTEP + vPAD;
    const trayDefault = { w: Math.max(trayMinW, Math.min(vPanel.width + 12, VW * 0.4)), h: Math.max(trayMinH, Math.min(vPanel.height + 12, VH * 0.5)) };
    const trayBox = tray.tw && tray.th
        ? { w: clamp(tray.tw * VW, trayMinW, VW), h: clamp(tray.th * VH, trayMinH, VH) }
        : trayDefault;
    const trayAutoSize = () => setTray((t) => ({ ...t, tw: Math.max(trayMinW, Math.min(vPanel.width + 12, VW * 0.95)) / VW, th: Math.max(trayMinH, Math.min(vPanel.height + 12, VH * 0.95)) / VH }));
    const trayResetSize = () => setTray((t) => ({ ...t, tw: null, th: null }));
    const maxTX = VW ? Math.max(0, 1 - trayBox.w / VW) : 0.9;
    const maxTY = VH ? Math.max(0, 1 - trayBox.h / VH) : 0.9;
    const trayLeft = clamp(tray.x, 0, maxTX) * VW;
    const trayTop = clamp(tray.y, 0, maxTY) * VH;
    const showTray = ((tray.visible && trayUnits.length > 0) || hForceTray) && tray.page === page && VW > 0;
    const trayViewH = trayBox.h - vHEADER - 4;
    const rowFrom = Math.max(0, Math.floor((trayScroll.t - vPAD) / vSTEP) - 1);
    const rowTo = Math.ceil((trayScroll.t + trayViewH) / vSTEP) + 1;
    const colFrom = Math.max(0, Math.floor((trayScroll.l - vPAD) / vSTEP) - 2);
    const colTo = Math.ceil((trayScroll.l + trayBox.w) / vSTEP) + 2;
    const laidTray = [];
    if (showTray) {
        for (let rr = rowFrom; rr <= Math.min(rowTo, vRows - 1); rr++) {
            for (let col = colFrom; col <= Math.min(colTo, vCOLS - 1); col++) {
                const u = trayUnits[rr * vCOLS + col];
                if (u) laidTray.push({ ...u, cx: vPAD + col * vSTEP + vD / 2, cy: vPAD + rr * vSTEP + vD / 2 });
            }
        }
    }

    const renderBubble = (u, baseUnit, inTray = false) => {
        const isActive = u.row_id === activeRowId && !tray.paused;
        const isSelected = selectedIds.includes(u.unitId);
        const st = inTray ? { ...effStyle(u.unitId), scale: trayScale, shape: "circle", rotation: 0, bw: null, bh: null, fontScale: 1 } : effStyle(u.unitId);
        const px = baseUnit * st.scale;
        const box = bubbleBox(u.label, px, st);
        const radius = st.shape === "square" ? "4px" : st.shape === "rounded" ? "30%" : st.shape === "diamond" ? "4px" : "9999px";
        const rot = (st.rotation || 0) + (st.shape === "diamond" ? 45 : 0);
        const paint = bubblePaint(st, u.color, px);
        const flashing = !inTray && flashSet.has(u.row_id);
        const ringCls = flashing ? "locate-flash z-50" : isSelected ? "ring-[3px] ring-amber-400 ring-offset-2 ring-offset-[#0b0f17] z-40" : isActive ? "marker-pulse ring-2 ring-white z-40" : "z-30";
        const deco = [st.underline && "underline", st.strike && "line-through"].filter(Boolean).join(" ") || "none";
        const canResize = isSelected && selectedIds.length === 1 && !!unitOffsets[u.unitId];
        return (
            <div key={u.unitId}
                data-testid={`position-marker-${u.label}`}
                data-flash={flashing ? "1" : "0"}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                data-selected={isSelected ? "1" : "0"}
                onMouseDown={(e) => startUnitDrag(e, u.unitId)}
                onDoubleClick={(e) => { e.stopPropagation(); returnToTray(u.unitId); }}
                onClick={(e) => { e.stopPropagation(); if (dragRef.current?.moved) return; const add = e.shiftKey || e.ctrlKey || e.metaKey; setSelectedIds((prev) => add ? (prev.includes(u.unitId) ? prev.filter((x) => x !== u.unitId) : [...prev, u.unitId]) : [u.unitId]); if (u.row_id) { suppressCenterRef.current = true; setActiveRowId(u.row_id); setTray((t) => (t.paused ? { ...t, paused: false } : t)); setTimeout(() => { suppressCenterRef.current = false; }, 0); } }}
                title={`${u.label} — ${t("pv_bubble_hint")}`}
                className={`absolute pointer-events-auto select-none flex flex-col font-mono shadow-lg cursor-move overflow-visible ${ringCls}`}
                style={{
                    left: u.cx, top: u.cy - px / 2, width: box.w, height: box.h,
                    transform: `translateX(-50%) rotate(${rot}deg)`,
                    backgroundColor: paint.fill ? rgba(paint.fill, paint.fillOpacity) : "transparent",
                    borderStyle: "solid", borderWidth: paint.borderWidth,
                    borderColor: rgba(paint.border, paint.borderOpacity),
                    boxShadow: paint.fill && paint.fillOpacity < 1 ? "none" : undefined,
                    color: st.textColor,
                    fontSize: box.fs, lineHeight: BUBBLE_LH, borderRadius: radius,
                    padding: `${box.padY}px ${box.padX}px`,
                    fontFamily: bubbleFont(st.fontFamily).css || undefined,
                    fontWeight: st.bold ? 700 : 400, fontStyle: st.italic ? "italic" : "normal", textDecoration: deco,
                    justifyContent: { top: "flex-start", bottom: "flex-end" }[st.alignV] || "center",
                    textAlign: st.alignH || "center",
                }}>
                <div className="overflow-hidden" style={{ maxHeight: "100%" }}>
                    {box.lines.map((l, i) => <span key={i} className="block whitespace-pre">{l}</span>)}
                </div>
                {canResize && (
                    <div data-testid={`bubble-resize-${u.label}`} title={t("pv_resize_hint")}
                        onMouseDown={(e) => startBubbleResize(e, u, px, box)}
                        className="absolute -right-1.5 -bottom-1.5 w-3 h-3 rounded-sm bg-white border-2 border-amber-400 cursor-nwse-resize" />
                )}
            </div>
        );
    };
    const TRAY_COLORS = ["#2563eb", "#10b981", "#f59e0b", "#f43f5e", "#a855f7", "#334155"];
    const bubbleStylesMap = bubbleStyles || {};
    const applyAll = tray.apply_all !== false;
    const base = bubbleBase(tray);
    const effStyle = (unitId) => effBubbleStyle(tray, bubbleStylesMap, unitId);
    const cur = (field) => {
        const id = selectedIds[0];
        if (!applyAll && id && bubbleStylesMap[id] && bubbleStylesMap[id][field] !== undefined)
            return bubbleStylesMap[id][field];
        return base[field];
    };
    const TRAY_STYLE_KEY = {
        scale: "bubble_scale", shape: "bubble_shape", bg: "bubble_color", textColor: "text_color", noFill: "no_fill", rotation: "bubble_rotation",
        bw: "bubble_w", bh: "bubble_h", fontScale: "font_scale", fontFamily: "font_family", bold: "font_bold", italic: "font_italic", underline: "font_underline",
        strike: "font_strike", alignH: "align_h", alignV: "align_v",
        borderColor: "border_color", borderWidth: "border_width", bgOpacity: "bg_opacity", borderOpacity: "border_opacity",
    };
    const scopeIds = (ids) => {
        if (bubbleScope !== "position") return ids;
        const labels = new Set(allUnits.filter((u) => ids.includes(u.unitId)).map((u) => u.label));
        return allUnits.filter((u) => labels.has(u.label)).map((u) => u.unitId);
    };
    const applyStyle = (field, value, ids = selectedIds) => {
        if (field === "bg" && !applyAll && ids.length && bubbleScope === "position" && colorMode(tray) === "position") {
            const labels = new Set(allUnits.filter((u) => ids.includes(u.unitId)).map((u) => u.label));
            setTray((tr) => {
                const pc = { ...(tr.pos_colors || {}) };
                labels.forEach((l) => { if (value) pc[l] = value; else delete pc[l]; });
                return { ...tr, pos_colors: pc, color_assign: "manual" };
            });
            setBubbleStyles((s) => { const n = { ...s }; scopeIds(ids).forEach((id) => { if (n[id]) { const { bg, ...rest } = n[id]; n[id] = rest; } }); return n; });
            return;
        }
        if (applyAll || ids.length === 0) setTray((t) => ({ ...t, [TRAY_STYLE_KEY[field]]: value }));
        else setBubbleStyles((s) => { const n = { ...s }; scopeIds(ids).forEach((id) => { n[id] = { ...(n[id] || {}), [field]: value }; }); return n; });
    };
    applyStyleRef.current = applyStyle;
    const startBubbleResize = (e, u, px, box) => {
        if (e.button !== 0) return;
        e.preventDefault(); e.stopPropagation();
        if (onInteractionStart) onInteractionStart();
        bResizeRef.current = { id: u.unitId, sx: e.clientX, sy: e.clientY, w0: box.w, h0: box.h, px };
    };
    const selLabel = units.find((u) => u.unitId === selectedIds[0])?.label || "";

    // Align the selected (placed) bubbles onto a straight line, keeping their
    // existing positions (gaps) on the other axis unchanged.
    const placedSelected = selectedIds.filter((id) => unitOffsets[id] && (unitOffsets[id].page || 1) === page);
    const alignSelected = (axis) => {
        const items = placedSelected.map((id) => ({ id, fx: unitOffsets[id].fx, fy: unitOffsets[id].fy }));
        if (items.length < 2) return;
        if (axis === "h") {
            const avgY = items.reduce((a, it) => a + it.fy, 0) / items.length;
            setUnitOffsets((prev) => {
                const next = { ...prev };
                items.forEach((it) => { next[it.id] = { ...next[it.id], fy: avgY }; });
                return next;
            });
        } else {
            const avgX = items.reduce((a, it) => a + it.fx, 0) / items.length;
            setUnitOffsets((prev) => {
                const next = { ...prev };
                items.forEach((it) => { next[it.id] = { ...next[it.id], fx: avgX }; });
                return next;
            });
        }
    };

    return (
        <div ref={rootRef} data-testid="pdf-viewer-root" className="h-full flex flex-col bg-[#0f172a]">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-3 py-2 bg-[#111827] border-b border-[#1e293b] gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                    <Button data-testid="tray-toggle" size="sm" variant={tray.visible ? "default" : "ghost"}
                        onClick={() => setTray((t) => ({ ...t, visible: !t.visible }))}
                        title={tray.visible ? t("pv_tray_hide") : t("pv_tray_show")}
                        className={tray.visible ? "bg-blue-600 hover:bg-blue-500 h-8" : "text-slate-400 h-8 hover:bg-[#182232]"}>
                        {tray.visible ? <Eye className="w-4 h-4 mr-1.5" /> : <EyeOff className="w-4 h-4 mr-1.5" />} {t("pv_tray")}
                    </Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button data-testid="tray-reset" size="sm" variant="ghost"
                                className="text-slate-400 h-8 hover:bg-[#182232]" title={t("pv_reset_confirm_title")}>
                                <RotateCcw className="w-4 h-4 mr-1.5" /> {t("pv_reset")}
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#111827] border-[#1e293b] text-slate-100">
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t("pv_reset_confirm_title")}</AlertDialogTitle>
                                <AlertDialogDescription className="text-slate-400">{t("pv_reset_confirm_desc")}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="reset-cancel" className="bg-transparent border-[#1e293b] text-slate-200 hover:bg-[#182232] hover:text-white">{t("pv_no")}</AlertDialogCancel>
                                <AlertDialogAction data-testid="reset-confirm" onClick={() => setUnitOffsets({})} className="bg-red-600 hover:bg-red-700 text-white">{t("pv_yes")}</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                    <div className="flex items-center gap-0.5 mx-1 border border-[#1e293b] rounded-md p-0.5">
                        <button data-testid="tool-pointer" onClick={() => { setTool("pointer"); setCTool(null); }} title={t("pv_tool_pointer")}
                            className={`h-7 w-7 flex items-center justify-center rounded transition-colors ${tool === "pointer" ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-[#182232]"}`}>
                            <MousePointer2 className="w-4 h-4" />
                        </button>
                        <button data-testid="tool-hand" onClick={() => { setTool("hand"); setCTool(null); }} title={t("pv_tool_hand")}
                            className={`h-7 w-7 flex items-center justify-center rounded transition-colors ${tool === "hand" ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-[#182232]"}`}>
                            <Hand className="w-4 h-4" />
                        </button>
                    </div>
                    {placedSelected.length >= 2 && (
                        <div className="flex items-center gap-0.5 mr-1 border border-[#1e293b] rounded-md p-0.5" data-testid="align-tools" title={t("pv_align_hint", { n: placedSelected.length })}>
                            <button data-testid="align-horizontal" onClick={() => alignSelected("h")} title={t("pv_align_h")}
                                className="h-7 w-7 flex items-center justify-center rounded text-slate-300 hover:bg-[#182232] hover:text-white transition-colors">
                                <AlignVerticalDistributeCenter className="w-4 h-4 rotate-90" />
                            </button>
                            <button data-testid="align-vertical" onClick={() => alignSelected("v")} title={t("pv_align_v")}
                                className="h-7 w-7 flex items-center justify-center rounded text-slate-300 hover:bg-[#182232] hover:text-white transition-colors">
                                <AlignHorizontalDistributeCenter className="w-4 h-4 rotate-90" />
                            </button>
                        </div>
                    )}
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button data-testid="tray-settings" size="sm" variant="ghost" className="text-slate-400 h-8 hover:bg-[#182232]">
                                <Settings2 className="w-4 h-4 mr-1.5" /> {t("pv_settings")}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 bg-[#111827] border-[#1e293b] text-slate-100 space-y-4 max-h-[80vh] overflow-y-auto">
                            <div className="font-mono font-semibold text-sm">{t("pv_set_title")}</div>

                            <BubbleScope t={t} hasSel={selectedIds.length > 0} posLabel={selLabel}
                                scope={applyAll ? "all" : bubbleScope}
                                setScope={(k) => { setTray((tr) => ({ ...tr, apply_all: k === "all" })); if (k !== "all") setBubbleScope(k); }} />
                            <div data-testid="bubble-editing-label" className="text-xs font-mono text-slate-400">
                                {applyAll ? `${t("pv_editing")}: ${t("pv_all_bubbles")}`
                                    : selectedIds.length >= 1 && bubbleScope === "position" ? `${t("pv_editing")}: ${t("pv_scope_position", { p: selLabel })}`
                                    : selectedIds.length === 1 ? `${t("pv_editing")}: ${selLabel} (${t("pv_selected_bubble")})`
                                        : selectedIds.length > 1 ? `${t("pv_editing")}: ${selectedIds.length} (${t("pv_selected_bubble")})`
                                            : t("pv_select_hint")}
                            </div>

                            <div className="flex items-center justify-between">
                                <Label className="text-sm text-slate-200">{t("pv_opaque")}</Label>
                                <Switch data-testid="tray-opaque" checked={!!tray.opaque}
                                    onCheckedChange={(v) => setTray((t) => ({ ...t, opaque: v }))} />
                            </div>
                            <div>
                                <Label className="text-sm text-slate-200">{t("pv_tray_bubble_size")}: {Math.round(trayScale * 100)}%</Label>
                                <div className="flex gap-1 mt-1.5">
                                    {[["s", 0.75], ["m", 1], ["l", 1.5]].map(([k, v]) => (
                                        <button key={k} data-testid={`tray-bubble-size-${k}`} onClick={() => setTray((tr) => ({ ...tr, tray_bubble_scale: v }))}
                                            className={`flex-1 h-7 rounded text-xs font-mono border transition-colors ${Math.abs(trayScale - v) < 0.01 ? "border-amber-400 text-amber-300 bg-amber-400/10" : "border-[#1e293b] text-slate-300 hover:bg-[#182232]"}`}>{t(`pv_bsize_${k}`)}</button>
                                    ))}
                                </div>
                                <div className="text-[10px] text-slate-500 mt-1.5">{t("pv_bsize_custom")}</div>
                                <Slider data-testid="tray-only-bubble-size" value={[trayScale]} min={0.5} max={2.5} step={0.1}
                                    onValueChange={(v) => setTray((tr) => ({ ...tr, tray_bubble_scale: v[0] }))} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-sm text-slate-200">{t("pv_bubble_size")}: {Math.round(cur("scale") * 100)}%</Label>
                                <div className="flex gap-1 mt-1.5">
                                    {[["s", 0.75], ["m", 1], ["l", 1.5]].map(([k, v]) => (
                                        <button key={k} data-testid={`bubble-size-${k}`} onClick={() => applyStyle("scale", v)}
                                            className={`flex-1 h-7 rounded text-xs font-mono border transition-colors ${Math.abs(cur("scale") - v) < 0.01 ? "border-amber-400 text-amber-300 bg-amber-400/10" : "border-[#1e293b] text-slate-300 hover:bg-[#182232]"}`}>{t(`pv_bsize_${k}`)}</button>
                                    ))}
                                </div>
                                <div className="text-[10px] text-slate-500 mt-1.5">{t("pv_bsize_custom")}</div>
                                <Slider data-testid="tray-bubble-size" value={[cur("scale")]} min={0.5} max={2.5} step={0.1}
                                    onValueChange={(v) => applyStyle("scale", v[0])} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-sm text-slate-200">{t("pv_rotation")}: {Math.round(cur("rotation"))}°</Label>
                                <Slider data-testid="bubble-rotation" value={[cur("rotation")]} min={0} max={360} step={15}
                                    onValueChange={(v) => applyStyle("rotation", v[0])} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-sm text-slate-200">{t("pv_shape")}</Label>
                                <Select value={cur("shape")} onValueChange={(v) => applyStyle("shape", v)}>
                                    <SelectTrigger data-testid="tray-shape" className="mt-1 h-8 bg-[#0b0f17] border-[#1e293b] text-slate-200">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#111827] border-[#1e293b] text-slate-100">
                                        <SelectItem value="circle">{t("pv_shape_circle")}</SelectItem>
                                        <SelectItem value="rounded">{t("pv_shape_rounded")}</SelectItem>
                                        <SelectItem value="square">{t("pv_shape_square")}</SelectItem>
                                        <SelectItem value="diamond">{t("pv_shape_diamond")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center justify-between">
                                <Label className="text-sm text-slate-200">{t("pv_no_fill")}</Label>
                                <Switch data-testid="bubble-no-fill" checked={!!cur("noFill")}
                                    onCheckedChange={(v) => applyStyle("noFill", v)} />
                            </div>
                            <ColorModeControls tray={tray} setTray={setTray} mode={colorMode(tray)} units={allUnits} t={t} />
                            <BubbleColorControls cur={cur} applyStyle={applyStyle} t={t}
                                showFill={!(colorMode(tray) === "position" && (applyAll || selectedIds.length === 0))} />
                            <div data-testid="bubble-text-format" className="space-y-2 border-t border-[#1e293b] pt-3">
                                <Label className="text-sm text-slate-200">{t("pv_font_family")}</Label>
                                <Select value={cur("fontFamily") || "default"} onValueChange={(v) => applyStyle("fontFamily", v)}>
                                    <SelectTrigger data-testid="bubble-font-family" className="h-8 bg-[#0b0f17] border-[#1e293b] text-slate-100"
                                        style={{ fontFamily: bubbleFont(cur("fontFamily")).css || undefined }}><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-[#111827] border-[#1e293b] text-slate-100">
                                        {BUBBLE_FONTS.map((f) => (
                                            <SelectItem key={f.key} value={f.key} data-testid={`bubble-font-${f.key}`} style={{ fontFamily: f.css || undefined }}>
                                                {f.key === "default" ? t("pv_font_default") : f.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Label className="text-sm text-slate-200">{t("pv_text_size")}: {Math.round(cur("fontScale") * 100)}%</Label>
                                <Slider data-testid="bubble-font-size" value={[cur("fontScale")]} min={0.3} max={3} step={0.05}
                                    onValueChange={(v) => applyStyle("fontScale", v[0])} />
                                <Label className="text-sm text-slate-200 block pt-1">{t("pv_text_format")}</Label>
                                <div className="flex gap-1">
                                    {[["bold", Bold], ["italic", Italic], ["underline", UnderlineIcon], ["strike", Strikethrough]].map(([f, Icon]) => (
                                        <button key={f} data-testid={`bubble-fmt-${f}`} onClick={() => applyStyle(f, !cur(f))}
                                            className={`h-7 w-7 flex items-center justify-center rounded border transition-colors ${cur(f) ? "bg-blue-600 border-blue-500 text-white" : "border-[#1e293b] text-slate-300 hover:bg-[#182232]"}`}>
                                            <Icon className="w-3.5 h-3.5" />
                                        </button>
                                    ))}
                                </div>
                                <Label className="text-sm text-slate-200 block pt-1">{t("pv_align")}</Label>
                                <div className="flex gap-1">
                                    {[["alignH", "left", AlignLeft], ["alignH", "center", AlignCenter], ["alignH", "right", AlignRight],
                                        ["alignV", "top", AlignVerticalJustifyStart], ["alignV", "middle", AlignVerticalJustifyCenter], ["alignV", "bottom", AlignVerticalJustifyEnd]].map(([f, v, Icon]) => (
                                        <button key={v} data-testid={`bubble-align-${v}`} onClick={() => applyStyle(f, v)}
                                            className={`h-7 w-7 flex items-center justify-center rounded border transition-colors ${cur(f) === v ? "bg-blue-600 border-blue-500 text-white" : "border-[#1e293b] text-slate-300 hover:bg-[#182232]"} ${v === "top" ? "ml-2" : ""}`}>
                                            <Icon className="w-3.5 h-3.5" />
                                        </button>
                                    ))}
                                </div>
                                <Label className="text-sm text-slate-200 block pt-1">{t("pv_bubble_w")}: {Math.round((cur("bw") || 1) * 100)}%</Label>
                                <Slider data-testid="bubble-width" value={[cur("bw") || 1]} min={0.15} max={8} step={0.05}
                                    onValueChange={(v) => applyStyle("bw", v[0])} />
                                <div className="flex items-center justify-between pt-1">
                                    <Label className="text-sm text-slate-200">{t("pv_bubble_h")}: {cur("bh") ? `${Math.round(cur("bh") * 100)}%` : t("pv_auto_h")}</Label>
                                    <Switch data-testid="bubble-auto-height" checked={!cur("bh")} onCheckedChange={(v) => applyStyle("bh", v ? null : 1)} />
                                </div>
                                {!!cur("bh") && (
                                    <Slider data-testid="bubble-height" value={[cur("bh")]} min={0.15} max={8} step={0.05}
                                        onValueChange={(v) => applyStyle("bh", v[0])} />
                                )}
                                <button data-testid="bubble-reset-size" onClick={() => { applyStyle("bw", null); applyStyle("bh", null); applyStyle("fontScale", 1); }}
                                    className="text-xs text-slate-400 hover:text-white underline">{t("pv_reset_size")}</button>
                            </div>
                            <div>
                                <Label className="text-sm text-slate-200">{t("pv_tray_color")}</Label>
                                <div className="flex gap-1.5 mt-1.5 flex-wrap">
                                    {TRAY_COLORS.map((c) => (
                                        <button key={c} onClick={() => setTray((t) => ({ ...t, color: c }))}
                                            style={{ backgroundColor: c }}
                                            className={`w-6 h-6 rounded-full border-2 hover:scale-110 transition-transform ${trayColor === c ? "border-white" : "border-white/30"}`} />
                                    ))}
                                </div>
                            </div>
                            <div className="text-[10px] font-mono text-slate-500 border-t border-[#1e293b] pt-2">{t("pv_spacing_hint")}</div>
                        </PopoverContent>
                    </Popover>
                </div>
                <div className="flex items-center gap-1 flex-nowrap shrink-0">
                    <Button data-testid="pdf-zoom-out" size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:bg-[#182232]"
                        onClick={() => zoomAt(1 / 1.2)}><ZoomOut className="w-4 h-4" /></Button>
                    <span className="text-xs font-mono text-slate-400 w-12 text-center">{Math.round(scale * 100)}%</span>
                    <Button data-testid="pdf-zoom-in" size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:bg-[#182232]"
                        onClick={() => zoomAt(1.2)}><ZoomIn className="w-4 h-4" /></Button>
                    <Button data-testid="pdf-zoom-fit" size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:bg-[#182232]"
                        onClick={() => setScale(1.1)}><Maximize className="w-4 h-4" /></Button>
                    <div className="flex items-center gap-1 ml-2">
                        <Button data-testid="pdf-page-prev" size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:bg-[#182232]"
                            disabled={page <= 1} onClick={() => { edgeRef.current = "top"; setPage((p) => Math.max(1, p - 1)); }}><ChevronLeft className="w-4 h-4" /></Button>
                        <PageInput page={page} numPages={numPages} onGo={(p) => { edgeRef.current = "top"; setPage(p); }} />
                        <Button data-testid="pdf-page-next" size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:bg-[#182232]"
                            disabled={page >= numPages} onClick={() => { edgeRef.current = "top"; setPage((p) => Math.min(numPages, p + 1)); }}><ChevronRight className="w-4 h-4" /></Button>
                        <Popover>
                            <PopoverTrigger asChild>
                                <button data-testid="page-name-btn" title={t("pv_rename_page")}
                                    className="flex items-center gap-1 text-xs font-mono text-slate-200 px-2 h-8 rounded border border-[#1e293b] hover:bg-[#182232] max-w-[110px] truncate">
                                    <Tag className="w-3 h-3 text-slate-400 shrink-0" />
                                    <span className="truncate">{(pageNames || {})[page] || t("pv_page", { n: page })}</span>
                                </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-64 bg-[#111827] border-[#1e293b] text-slate-100">
                                <Label className="text-sm text-slate-200">{t("pv_page_name_title", { n: page })}</Label>
                                <Input data-testid="page-name-input" value={(pageNames || {})[page] || ""}
                                    onChange={(e) => setPageNames((p) => ({ ...p, [page]: e.target.value }))}
                                    placeholder={t("pv_page_name_ph")}
                                    className="mt-1.5 h-8 bg-[#0b0f17] border-[#1e293b] text-slate-100" />
                                <p className="text-[11px] text-slate-500 mt-2">{t("pv_page_name_hint")}</p>
                            </PopoverContent>
                        </Popover>
                    </div>
                    {!readOnly && <Button data-testid="ai-analyze-btn" size="sm" onClick={runAi} disabled={aiBusy}
                        className="h-8 ml-2 bg-gradient-to-r from-fuchsia-600 to-blue-600 hover:from-fuchsia-500 hover:to-blue-500 text-white">
                        {aiBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />} {t("pv_ai")}
                    </Button>}
                    {onOpenAiHistory && <Button data-testid="ai-history-btn" size="sm" variant="outline" onClick={onOpenAiHistory} title={t("ah_title")}
                        className="h-8 ml-1 bg-transparent border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/10">{t("ah_btn")}</Button>}
                    {hasAi && !readOnly && (
                        <Button data-testid="ai-clear-btn" size="sm" variant="ghost" onClick={onClearAi}
                            title={t("ai_clear")}
                            className="h-8 ml-1 text-slate-400 hover:bg-[#182232] hover:text-rose-300">
                            <Eraser className="w-4 h-4 mr-1.5" /> {t("ai_clear")}
                        </Button>
                    )}
                    <input ref={changeInputRef} type="file" accept="application/pdf" className="hidden"
                        onChange={(e) => { if (e.target.files[0]) { onUpload(e.target.files[0]); e.target.value = ""; } }} />
                    {!readOnly && <AlertDialog open={changeConfirm} onOpenChange={setChangeConfirm}>
                        <AlertDialogTrigger asChild>
                            <button data-testid="pv-change-btn"
                                className="ml-2 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-100 border border-[#1e293b] rounded-md px-2 py-1.5 cursor-pointer hover:bg-[#182232]">
                                {(uploading || exporting) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {t("pv_change")}
                            </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#111827] border-[#1e293b] text-slate-100">
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t("pv_change_confirm_title")}</AlertDialogTitle>
                                <AlertDialogDescription className="text-slate-400">{t("pv_change_confirm_desc")}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="pv-change-cancel" className="bg-transparent border-[#1e293b] text-slate-200 hover:bg-[#182232] hover:text-white">{t("pv_change_cancel")}</AlertDialogCancel>
                                <Button data-testid="pv-change-skip" variant="outline"
                                    className="bg-[#0b0f17] border-[#1e293b] text-slate-200 hover:bg-[#182232]"
                                    onClick={() => { setChangeConfirm(false); changeInputRef.current?.click(); }}>
                                    {t("pv_change_skip")}
                                </Button>
                                <AlertDialogAction data-testid="pv-change-export" className="bg-blue-600 hover:bg-blue-500 text-white"
                                    onClick={() => { onExportPdf?.(); changeInputRef.current?.click(); }}>
                                    {t("pv_change_export")}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>}
                </div>
            </div>

            {/* Canvas area */}
            <div className="flex-1 relative overflow-hidden">
                {aiReview && aiMarking && (<>
                    <div data-testid="ai-marking-frame" className="absolute inset-0 z-[40] pointer-events-none ai-mark-frame" />
                    <div data-testid="ai-marking-banner" className="absolute top-3 left-1/2 -translate-x-1/2 z-[41] pointer-events-none px-4 py-1.5 rounded-full bg-fuchsia-600 text-white text-xs font-semibold shadow-lg flex items-center gap-2">
                        {t("ar_mark_banner")} <span className="opacity-70 font-normal">· Esc</span>
                    </div>
                </>)}
                {cm && (
                    <CommentToolbar tool={cTool} setTool={setCTool} styleFor={styleFor} setStyle={setCStyle}
                        selected={(comments || []).find((c) => c.id === selectedCommentId)} cm={cm}
                        multi={(comments || []).filter((c) => cMulti.includes(c.id))}
                        visible={showComments} setVisible={setShowComments} t={t} />
                )}
                <div ref={scrollRef} data-testid="pdf-viewer-canvas" className="absolute inset-0 overflow-auto bg-[#0f172a] p-6 pl-16 select-none">
                    {loading && <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>}
                    <div ref={wrapperRef} data-testid="pdf-page-wrapper" data-scale={scale} onMouseDown={startPan} className={`relative mx-auto shadow-2xl select-none ${tool === "hand" ? "cursor-grab active:cursor-grabbing" : "cursor-default"}`} style={{ width: W, height: H }}>
                        <canvas ref={canvasRef} draggable={false} onDragStart={(e) => e.preventDefault()} className="block bg-white select-none" />
                        {/* Bubbles placed on the drawing (scroll with it) */}
                        {tray.visible && W > 0 && laidPlaced.map((u) => renderBubble(u, W * 0.03))}
                        {/* AI field overlays: colored boxes for detected fields on this page */}
                        {W > 0 && (rows || []).filter((r) => r.ai && r.ai.page === page).map((r) => (
                            ["name", "width", "height", "qty"].map((f) => {
                                const b = r.ai.boxes && r.ai.boxes[f];
                                if (!b || b.length !== 4) return null;
                                const left = b[0] * W, top = b[1] * H;
                                const bw = Math.max(8, (b[2] - b[0]) * W), bh = Math.max(8, (b[3] - b[1]) * H);
                                const active = r.id === activeRowId;
                                const col = AI_FIELD_COLORS[f];
                                return (
                                    <div key={`${r.id}-${f}`} data-testid={`ai-box-${f}-${r.id}`} data-flash={flashSet.has(r.id) ? "1" : "0"}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => { e.stopPropagation(); setActiveRowId(r.id); }}
                                        title={`${r.position || ""} · ${f}`}
                                        className={`absolute pointer-events-auto cursor-pointer transition-shadow ${flashSet.has(r.id) ? "locate-flash" : ""}`}
                                        style={{
                                            left, top, width: bw, height: bh,
                                            border: `2px solid ${col}`, backgroundColor: `${col}22`, borderRadius: 2,
                                            boxShadow: active ? `0 0 0 2px ${col}, 0 0 10px ${col}` : "none",
                                            zIndex: active ? 46 : 22,
                                        }} />
                                );
                            })
                        ))}
                        {aiReview && W > 0 && (
                            <AiReviewOverlay W={W} H={H} page={page} review={aiReview} marking={aiMarking} setMarking={setAiMarking}
                                textItems={textItems} textEnabled={tool === "pointer"}
                                canvasRef={canvasRef} projectId={projectId} t={t} />
                        )}
                        {marqueeRect && tool !== "hand" && (
                            <div data-testid="marquee-rect" className="absolute border-2 border-amber-400 bg-amber-400/10 pointer-events-none z-30"
                                style={{ left: marqueeRect.x, top: marqueeRect.y, width: marqueeRect.w, height: marqueeRect.h }} />
                        )}
                        {cm && (
                            <CommentLayer W={W} H={H} zoom={scale} page={page} pdfDoc={pdfDoc} comments={comments || []}
                                tool={showComments ? cTool : null} setTool={setCTool} styleFor={styleFor}
                                selectedId={selectedCommentId} setSelectedId={setSelectedCommentId} cm={cm}
                                onReply={onReplyComment} visible={showComments} dateFmt={dateFmt} t={t}
                                multiIds={cMulti} setMultiIds={setCMulti} />
                        )}
                    </div>
                </div>

                {/* Tray + unplaced bubbles stay fixed in the viewport */}
                <div className="absolute inset-0 pointer-events-none z-30">
                    {showTray && (
                        <div data-testid="bubble-tray" ref={trayElRef}
                            className={`absolute pointer-events-auto rounded-xl border-2 ${tray.opaque ? "border-solid bg-[#0b0f17]" : "border-dashed bg-white/10 backdrop-blur-[2px]"}`}
                            data-tw={Math.round(trayBox.w)} data-th={Math.round(trayBox.h)}
                            style={{ left: trayLeft, top: trayTop, width: trayBox.w, height: trayBox.h, borderColor: trayColor }}>
                            <div data-testid="bubble-tray-handle" onMouseDown={startTrayDrag}
                                className="flex items-center gap-1.5 px-2 cursor-move select-none text-white rounded-t-[10px] font-mono"
                                style={{ height: vPanel.header, fontSize: Math.max(9, vPanel.header * 0.42), backgroundColor: trayColor }}>
                                <Grip className="w-3.5 h-3.5 shrink-0" /> <Move className="w-3 h-3 shrink-0" />
                                <span className="truncate flex-1">{t("pv_tray_label", { n: unplacedCount })}</span>
                                <button data-testid="tray-auto-size" title={t("pv_tray_auto")} onMouseDown={(e) => e.stopPropagation()} onClick={trayAutoSize}
                                    className="shrink-0 p-0.5 rounded hover:bg-white/20 transition-colors"><Scan className="w-3.5 h-3.5" /></button>
                                <button data-testid="tray-reset-size" title={t("pv_tray_reset")} onMouseDown={(e) => e.stopPropagation()} onClick={trayResetSize}
                                    className="shrink-0 p-0.5 rounded hover:bg-white/20 transition-colors"><RotateCcw className="w-3.5 h-3.5" /></button>
                            </div>
                            <div data-testid="bubble-tray-scroll" ref={setTraySc} onScroll={onTrayScroll}
                                className="tray-scroll relative overflow-auto" style={{ height: trayViewH }}>
                                <div className="relative" style={{ width: vPanel.width, height: vPanel.height - vHEADER }}>
                                    {tray.visible && laidTray.map((u) => renderBubble(u, TRAY_BUBBLE_PX, true))}
                                </div>
                            </div>
                            <div data-testid="tray-resize-handle" title={t("pv_tray_resize")}
                                onMouseDown={(e) => startTrayResize(e, trayBox, trayMinW, trayMinH)}
                                className="absolute -right-1.5 -bottom-1.5 w-4 h-4 rounded-sm bg-white border-2 cursor-nwse-resize z-50"
                                style={{ borderColor: trayColor }} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
